import { useCallback, useEffect, useRef, useState } from "react";
import {
  asAlreadyRunning,
  asBacklogTooLarge,
  asSweepNotPreviewed,
  fetchCatalogRun,
  startCatalogRun,
  startCatalogSweep,
} from "@/services/nvgCatalogApi";
import type {
  BacklogTooLarge,
  CatalogRunStarted,
  CatalogRunState,
  CatalogRunTarget,
  SweepNotPreviewed,
} from "@/types/nvgCatalog";

const POLL_INTERVAL_MS = 2000;

/**
 * Statuses after which polling stops. Only two of these six are "the thing
 * worked" / "the thing broke"; the rest are outcomes that will not change on a
 * further poll. `already_running` means the box's lock was held so nothing was
 * attempted, `interrupted` means Spot reclaimed the task, `refused` means a
 * sweep's safety floor rejected the plan without writing anything, and
 * `partial` means a sweep deleted its rows but leaked some S3 objects.
 *
 * Leaving any of them out would poll forever against a run that has finished.
 */
const TERMINAL_STATUSES = new Set<CatalogRunState["status"]>([
  "succeeded",
  "failed",
  "already_running",
  "interrupted",
  "refused",
  "partial",
]);

export interface CatalogRunHookState {
  phase: "idle" | "submitting" | "polling" | "done" | "error";
  run: CatalogRunState | null;
  /** Set when a delta run was refused; the panel renders this as a button. */
  backlogTooLarge: BacklogTooLarge | null;
  /**
   * Set when a sweep was refused because its confirmation no longer matches a
   * current plan. Like `backlogTooLarge` this is not an error state — the
   * panel re-previews and shows the operator what moved.
   */
  sweepNotPreviewed: SweepNotPreviewed | null;
  error: string | null;
}

const IDLE: CatalogRunHookState = {
  phase: "idle",
  run: null,
  backlogTooLarge: null,
  sweepNotPreviewed: null,
  error: null,
};

/**
 * Submit-then-poll for an operator catalog run (tools plan B4).
 *
 * Modelled on `useNvgBake` — same 2 s interval, same terminal-status set, same
 * cleanup-on-unmount — with one difference that matters: a run has two possible
 * status sources. `delta` and `sweep` runs poll SSM and a `bulk` run polls ECS.
 * The kind is taken from the POST response and carried into every poll rather
 * than inferred from the id, because the two id shapes (an SSM command UUID and
 * an ECS task ARN) are not something the client should be parsing.
 *
 * `startSweep` is separate from `start` rather than a third target on it: a
 * sweep takes a confirmation and no limit, and the confirmation must not be
 * optional in the type.
 */
export function useCatalogRun(onSettled?: () => void) {
  const [state, setState] = useState<CatalogRunHookState>(IDLE);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeRunIdRef = useRef<string | null>(null);
  // Kept in a ref so the settle callback can change between renders without
  // tearing down a running poll.
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const startPolling = useCallback(
    (runId: string, kind: CatalogRunTarget) => {
      stopPolling();

      pollIntervalRef.current = setInterval(async () => {
        // A run started after this interval was scheduled owns the state now.
        if (activeRunIdRef.current !== runId) {
          stopPolling();
          return;
        }

        try {
          const run = await fetchCatalogRun(runId, kind);
          if (activeRunIdRef.current !== runId) return;

          setState((prev) => ({ ...prev, run }));

          if (TERMINAL_STATUSES.has(run.status)) {
            stopPolling();
            setState((prev) => ({ ...prev, phase: "done", run }));
            // The backlog and staleness numbers are stale the moment a run
            // finishes, so the panel refetches them here rather than making an
            // operator reload to find out whether it helped.
            onSettledRef.current?.();
          }
        } catch (err) {
          if (activeRunIdRef.current !== runId) return;
          stopPolling();
          setState((prev) => ({
            ...prev,
            phase: "error",
            error: err instanceof Error ? err.message : "Polling failed",
          }));
        }
      }, POLL_INTERVAL_MS);
    },
    [stopPolling],
  );

  /**
   * The submit-then-poll body every target shares. Only the POST differs, so
   * it is passed in — a sweep sends a confirmation instead of a limit, and
   * folding that into one signature would make the confirmation optional.
   */
  const submit = useCallback(
    async (dispatch: () => Promise<CatalogRunStarted>) => {
      stopPolling();
      activeRunIdRef.current = null;
      setState({ ...IDLE, phase: "submitting" });

      try {
        const started = await dispatch();
        activeRunIdRef.current = started.run_id;
        setState({
          ...IDLE,
          phase: "polling",
          run: {
            kind: started.kind,
            run_id: started.run_id,
            status: "pending",
            terminal: false,
            exit_code: null,
            detail: null,
            log_group: started.log_group,
            log_stream: started.log_stream,
          },
        });
        // Keyed on the kind the server reported, not on what was asked for —
        // they agree today, and this stays correct if the server ever
        // redirects one target to another.
        startPolling(started.run_id, started.kind);
      } catch (err) {
        const backlog = asBacklogTooLarge(err);
        if (backlog !== null) {
          // Not an error state. The operator asked for the right thing against
          // the wrong target, and the remedy is one click away.
          setState({ ...IDLE, phase: "idle", backlogTooLarge: backlog });
          return;
        }

        const notPreviewed = asSweepNotPreviewed(err);
        if (notPreviewed !== null) {
          // Also not an error state: the plan moved between the preview and
          // the apply, which is exactly what the server-side check exists to
          // catch. The panel re-previews and shows what changed.
          setState({ ...IDLE, phase: "idle", sweepNotPreviewed: notPreviewed });
          return;
        }

        const busy = asAlreadyRunning(err);
        setState({
          ...IDLE,
          phase: "error",
          error:
            busy !== null
              ? `A ${busy.target} run is already in progress.`
              : err instanceof Error
                ? err.message
                : "Failed to start the run",
        });
      }
    },
    [stopPolling, startPolling],
  );

  const start = useCallback(
    (target: CatalogRunTarget, limit: number | null) =>
      submit(() => startCatalogRun(target, limit)),
    [submit],
  );

  /**
   * Start an orphan sweep against a plan the operator has read.
   *
   * `confirmReap` is the number they were shown as due for deletion. The
   * server checks it against its own cached plan and refuses any mismatch, so
   * passing a stale number fails loudly rather than deleting the wrong set.
   */
  const startSweep = useCallback(
    (confirmReap: number) => submit(() => startCatalogSweep(confirmReap)),
    [submit],
  );

  const reset = useCallback(() => {
    stopPolling();
    activeRunIdRef.current = null;
    setState(IDLE);
  }, [stopPolling]);

  return { state, start, startSweep, reset };
}
